import { ApiRoutes, AppPaginationOptions, QueryError, WithPagination } from '@/common/interfaces';
import { apiExecutor, buildMutation, buildQuery } from '@/common/services';

import { PageNode } from '@/modules/Pages';
import {
    BulkCreateRedirectsByPagesPayload,
    BulkCreateRedirectsByPathsPayload,
    BulkCreateRedirectsResult,
    BulkDeleteRedirectsPayload,
    BulkUpdateRedirectsActivityPayload,
    Redirect,
    RedirectConflicts,
    RedirectFormFields,
    RedirectsQueryKeys,
} from '@/modules/Sites';

export const useGetAllRedirectsQuery = (siteId: string, paginationOptions: AppPaginationOptions) =>
  buildQuery<WithPagination<Redirect>, QueryError>([RedirectsQueryKeys.GetAll, siteId, paginationOptions], async () => {
    const { data } = await apiExecutor.get(
      ApiRoutes.Redirects.replace(':siteId', siteId),
      { params: paginationOptions }
    );
    return data;
  });

export const useGetRedirectsConflictsQuery = (siteId: string) =>
  buildQuery<RedirectConflicts, QueryError>([RedirectsQueryKeys.Conflicts, siteId], async () => {
    const { data } = await apiExecutor.get(ApiRoutes.RedirectsConflicts.replace(':siteId', siteId));
    return data;
  });

export const useGetRedirectsPagesPathsQuery = (
  siteId: string,
  paginationOptions: AppPaginationOptions,
  search?: string
) =>
  buildQuery<WithPagination<Pick<PageNode, 'id' | 'path'>>, QueryError>(
    [RedirectsQueryKeys.PagesPaths, siteId, { ...paginationOptions, search }],
    async () => {
      const { data } = await apiExecutor.get(ApiRoutes.RedirectsPagesPaths.replace(':siteId', siteId), {
        params: { ...paginationOptions, search },
      });
      return data;
    }
  );

export const useGetByIdRedirectsQuery = (siteId: string, id: string) =>
  buildQuery<Redirect, QueryError>([RedirectsQueryKeys.Dossier, siteId, id], async () => {
    const { data } = await apiExecutor.get(
      ApiRoutes.RedirectsById.replace(':siteId', siteId).replace(':redirectId', id)
    );
    return data;
  });

export const useCreateRedirectsMutation = (siteId: string) =>
  buildMutation<Redirect, QueryError, RedirectFormFields>([RedirectsQueryKeys.Create, siteId], async (postData) => {
    const { data } = await apiExecutor.post(ApiRoutes.Redirects.replace(':siteId', siteId), postData);
    return data;
  });

export const useEditRedirectsMutation = (siteId: string, id: string) =>
  buildMutation<Redirect, QueryError, Partial<RedirectFormFields>>([RedirectsQueryKeys.Edit, siteId, id], async (postData) => {
    const { data } = await apiExecutor.patch(
      ApiRoutes.RedirectsById.replace(':siteId', siteId).replace(':redirectId', id),
      { ...postData, id }
    );
    return data;
  });

export const useDeleteRedirectsMutation = (siteId: string, id: string) =>
  buildMutation<void, QueryError, void>([RedirectsQueryKeys.Delete, siteId, id], async () => {
    await apiExecutor.delete<void>(ApiRoutes.RedirectsById.replace(':siteId', siteId).replace(':redirectId', id));
  });

export const useBreadcrumbRedirectsQuery = (siteId: string, id: string) =>
  buildQuery<string, QueryError>([RedirectsQueryKeys.Breadcrumb, siteId, id], async () => {
    const { data } = await apiExecutor.get(ApiRoutes.RedirectsBreadcrumb.replace(':siteId', siteId).replace(':redirectId', id));
    return data;
  });

export const useBulkCreateRedirectsByPagesMutation = (siteId: string) =>
  buildMutation<BulkCreateRedirectsResult, QueryError, BulkCreateRedirectsByPagesPayload>(
    [RedirectsQueryKeys.BulkCreateByPages, siteId],
    async (payload) => {
      const { data } = await apiExecutor.post(ApiRoutes.RedirectsBulkByPages.replace(':siteId', siteId), payload);
      return data;
    }
  );

export const useBulkCreateRedirectsByPathsMutation = (siteId: string) =>
  buildMutation<BulkCreateRedirectsResult, QueryError, BulkCreateRedirectsByPathsPayload>(
    [RedirectsQueryKeys.BulkCreateByPaths, siteId],
    async (payload) => {
      const { data } = await apiExecutor.post(ApiRoutes.RedirectsBulkByPaths.replace(':siteId', siteId), payload);
      return data;
    }
  );

export const useBulkUpdateRedirectsActivityMutation = (siteId: string) =>
  buildMutation<{ updatedCount: number }, QueryError, BulkUpdateRedirectsActivityPayload>(
    [RedirectsQueryKeys.BulkActivity, siteId],
    async (payload) => {
      const { data } = await apiExecutor.patch(ApiRoutes.RedirectsBulkActivity.replace(':siteId', siteId), payload);
      return data;
    }
  );

export const useBulkDeleteRedirectsMutation = (siteId: string) =>
  buildMutation<void, QueryError, BulkDeleteRedirectsPayload>(
    [RedirectsQueryKeys.BulkDelete, siteId],
    async (payload) => {
      await apiExecutor.post<void>(ApiRoutes.RedirectsBulkDelete.replace(':siteId', siteId), payload);
    }
  );
